import React, { useState, useEffect } from "react";
import { FaCheck, FaTrash } from "react-icons/fa";
import ProductModal from "../../Modals/ProductModal";
import DeleteConfirmationModal from "../../Modals/DeleteConfirmationModal";
import SelectProductVeriation from "./SelectProductVeriation";
import { listFoldersFromS3, deleteFromS3, generatePresignedUrl, listFileNamesFromS3 } from "../../../config/aws";
import { USER_NAME } from "../../../constant";
import { useImage } from "../../../context/ImageContext";
import { showSuccessAlert, showErrorAlert, showWarningAlert, showInfoAlert, showConfirmDialog } from '../../../utils/sweetAlertConfig';
import '../../../styles/sweetAlert.css';

const ProductGallery = ({ setIsProductModalOpen, selectedSubProduct, handleProductSelect, setRefNum, setCreditsAvailable, setProductPrompt }) => {
  const [activeTab, setActiveTab] = useState("myProducts");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductName, setSelectedProductName] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showVariations, setShowVariations] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [myProducts, setMyProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const { selectedImage } = useImage();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);


  const fetchProducts = async (USER_NAME, setMyProductsState) => {
    try {
      const productFolders = await listFoldersFromS3(`USERS/${USER_NAME}/all_products/`);
      // Transform folder names into product objects
      const products = productFolders.map(folderName => ({
        name: folderName,
        src: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTUUGMO8v1YxhmAmNfQ_3xg-LGZ8c3emfX6_A&s" // Default image
      }));

      // Fetch the first image for each product folder to use as thumbnail
      const productsWithImages = await Promise.all(products.map(async (product) => {
        try {
          const thumbnailPath = `USERS/${USER_NAME}/all_products/${product.name}/user_product_images/thumbnail.webp`;
          const preSignedURL = await generatePresignedUrl(thumbnailPath);
          product.src = preSignedURL;
          return product;
        } catch (error) {
          console.error(`Error fetching thumbnail images for product ${product.name}:`, error);
          return product;
        }
      }));
      setMyProductsState(productsWithImages);
    } catch (error) {
      console.error("Error fetching product folders:", error);
    }
  };



  // Fetch product folders from S3 when component mounts
  useEffect(() => {
    fetchProducts(USER_NAME, setMyProducts);
    fetchProducts("default_user", setProducts);
  }, []);

  useEffect(() => {
    handleProductSelect({ name: selectedSubProduct.name, src: selectedImage.src });
  }, [selectedImage]);

  const handleProductSelection = (index) => {
    setSelectedProduct(index);
    setShowVariations(true);

    if (activeTab === "myProducts") {
      setSelectedProductName(filteredMyProducts[index].name);
      handleProductSelect({ name: filteredMyProducts[index]['name'], src: selectedImage.src });
    } else {
      setSelectedProductName(filteredProducts[index].name);
      handleProductSelect({ name: filteredProducts[index]['name'], src: selectedImage.src });
    }
  };

  const handleBack = () => {
    setShowVariations(false);
    setSelectedProduct(null);
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredMyProducts = myProducts.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDeleteClick = (e, productName) => {
    e.stopPropagation();
    setProductToDelete(productName);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      // console.log('Starting deletion process for product:', productToDelete);
      const deletePath = `USERS/${USER_NAME}/all_products/${productToDelete}`;
      // console.log('Delete path:', deletePath);

      const success = await deleteFromS3(deletePath);
      // console.log('Delete operation result:', success);

      if (success) {
        await fetchProducts(USER_NAME, setMyProducts);
        setDeleteModalOpen(false);
        setProductToDelete(null);
        showSuccessAlert('Product Deleted!', 'The product has been successfully deleted.', 'The product list has been updated');
      } else {
        console.error('Delete operation returned false');
        showErrorAlert('Failed to delete product. Please try again.', 'Deletion Failed', 'Please check the console for details');
      }
    } catch (error) {
      console.error('Error in handleDeleteConfirm:', {
        message: error.message,
        productName: productToDelete
      });
      showErrorAlert('An error occurred while deleting the product.', 'Error', 'Please check the console for details');
    }
  };

  const renderStyleGrid = (filteredProducts, isDefaultProducts = false) => (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {filteredProducts.map((product, index) => (
        <div
          key={index}
          className="flex flex-col cursor-pointer group m-0.5"
          onClick={() => handleProductSelection(index)}
        >
          <div className="relative overflow-hidden aspect-square w-28 h-28">
            <img
              src={product.src || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTUUGMO8v1YxhmAmNfQ_3xg-LGZ8c3emfX6_A&s"}
              alt={product.name}
              className={`w-full h-full object-cover rounded-xl transition-opacity ${selectedProduct === index ? 'opacity-75' : ''}`}
              title={product.name}
            />
            {selectedProduct === index && (
              <div className="absolute top-1 right-1 bg-green-500 rounded-full p-1">
                <FaCheck className="text-white text-xs" />
              </div>
            )}
            {!isDefaultProducts && (
              <button
                onClick={(e) => handleDeleteClick(e, product.name)}
                className="absolute top-1 left-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
              >
                <FaTrash className="text-xs" />
              </button>
            )}
          </div>
          <div className="text-center text-black font-semibold text-xs mt-1 w-28 truncate">{product.name}</div>
        </div>
      ))}
      {filteredProducts.length === 0 && (
        <div className="col-span-full text-center text-gray-500">
          No products found matching your search.
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="bg-white rounded-lg p-6 w-[500px] h-full flex flex-col">
        {!showVariations ? (
          <>
            {/* Tabs */}
            <div className="flex justify-between items-center border-b border-gray-200 pb-2 mb-4">
              <div className="flex w-full justify-between">                
                <button
                  className={`py-2 px-2 font-semibold border-b-2 transition-colors flex-1 text-purple-600 border-purple-600`}
                >
                  My Products
                </button>
              </div>
            </div>

            <div className="relative mb-6">
              <input
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                placeholder="Search for products"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <i className="fas fa-search absolute left-3 top-3 text-gray-400" />
            </div>

            {/* Product Display */}
            <div className="flex-1 overflow-y-auto pr-2 -mr-2">
              {activeTab === "default" ? (
                renderStyleGrid(filteredProducts, true)
              ) : (
                <>
                  <div className="text-center mb-4 py-2 px-6 bg-gray-50 rounded-xl border border-dashed border-gray-300 shadow-inner">
                    {myProducts.length === 0 && (<p className="text-gray-600 font-medium mb-3 text-lg">No products found.</p>)}
                    <p className="text-gray-500 ">
                      Click <button onClick={() => setShowModal(true)} className="text-purple-600 font-semibold hover:text-purple-800 transition-colors duration-300 underline hover:no-underline focus:outline-none">here</button> to add your product!
                    </p>
                  </div>
                  {myProducts.length > 0 && renderStyleGrid(filteredMyProducts, false)}
                </>
              )}
            </div>
          </>
        ) : (
          <SelectProductVeriation
            onBack={handleBack}
            setIsProductModalOpen={setIsProductModalOpen}
            selectedProduct={selectedProductName}
            handleProductSelect={handleProductSelect}
            setRefNum={setRefNum}
            setProductPrompt={setProductPrompt}
          />
        )}
      </div>
      
      {/* model placeholder */}
      <ProductModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        fetchProducts={fetchProducts}
        setMyProducts={setMyProducts}
        setCreditsAvailable={setCreditsAvailable}
        setProductPrompt={setProductPrompt} 
        setIsProductModalOpen={setIsProductModalOpen}
        handleProductSelect={handleProductSelect}
        setRefNum={setRefNum}
      />
        
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setProductToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        productName={productToDelete}
      />
    </>
  );
};

export default ProductGallery;
